import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type {
  Room,
  SchemeContent,
  VilmerActivitiesResult,
} from "@shared/types/foyer";
import { foyerApi } from "../../http/foyer";
import { usePremiseScope } from "../../context/PremiseScopeContext";
import { activityDate, activityTimeRange } from "@shared/vilmer";

interface Props {
  content: SchemeContent;
  /** The premise's rooms - the test button needs one to borrow a token from. */
  rooms: Room[];
  onChange: (next: SchemeContent) => void;
}

/**
 * VilMer connection settings.
 *
 * Deliberately small, because the interesting half of this configuration is
 * NOT here: the API address is one value for the whole building, while the
 * integration token is per ROOM (Rooms → ⋯ → Integration token), because
 * VilMer issue tokens per room, per ward or per company and the room is the
 * only level that can carry all three. This pane says so out loud rather than
 * leaving an operator to find out from an empty TV.
 */
export default function VilmerEditor({ content, rooms, onChange }: Props) {
  const premiseId = usePremiseScope();
  const v = content.vilmer ?? {};
  const [url, setUrl] = useState(v.apiUrl ?? "");
  const [roomId, setRoomId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<
    (VilmerActivitiesResult & { usingFixtures?: boolean }) | null
  >(null);

  const withToken = rooms.filter((r) => r.integrationId?.trim());
  const testRoom = roomId || withToken[0]?.id || "";

  const set = (part: Partial<typeof v>) =>
    onChange({ ...content, vilmer: { ...v, ...part } });

  const test = async () => {
    if (!premiseId || !testRoom) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const out = await foyerApi.previewVilmer(premiseId, {
        url: url.trim() || undefined,
        roomId: testRoom,
      });
      setResult(out);
      // A successful read is also the operator's intent to use this address.
      // They still have to Save the premise for it to take effect.
      if (url.trim()) set({ apiUrl: url.trim() });
    } catch (err) {
      setError(
        vilmerErrorMessage((err as { code?: string } | undefined)?.code)
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={2} sx={{ maxWidth: 720 }}>
      <Typography variant="body2" color="text.secondary">
        Activities a resident can take part in, shown inside the Information
        screen. Focusing VilMer on the TV already lists today&apos;s programme —
        OK steps into it, and OK on an activity opens the full description.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              VilMer API address
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The address VilMer provide for this customer. One address for the
              whole building — the per-room token below decides which unit each
              room shows. The token is sent as a bearer header; if their API
              wants it in the path instead, put{" "}
              <Box
                component="code"
                sx={{
                  fontFamily:
                    'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
                  bgcolor: "action.hover",
                  px: 0.5,
                  borderRadius: 0.5,
                }}
              >
                {"{integrationId}"}
              </Box>{" "}
              in the address and each room&apos;s token is substituted in.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              size="small"
              fullWidth
              label="API address"
              placeholder="https://api.vilmer.no/foyer/activities"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              inputProps={{ "data-testid": "vilmer-api-url" }}
            />
            <TextField
              size="small"
              select
              label="Refresh"
              value={v.refreshMinutes ?? 30}
              onChange={(e) => set({ refreshMinutes: Number(e.target.value) })}
              sx={{ minWidth: 150 }}
              SelectProps={{ native: true }}
            >
              <option value={5}>Every 5 min</option>
              <option value={30}>Every 30 min</option>
              <option value={120}>Every 2 hours</option>
              <option value={1440}>Daily</option>
            </TextField>
          </Stack>

          <Divider />

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Room tokens
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Each room carries the integration token VilMer issued — set it
              under <strong>Rooms → ⋯ → Integration token</strong>. Rooms on the
              same ward usually share one token; paste the same value into each.
              A room without a token shows &ldquo;not connected yet&rdquo;
              rather than an empty week.
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Chip
                size="small"
                color={withToken.length ? "success" : "default"}
                label={`${withToken.length} of ${rooms.length} rooms have a token`}
              />
            </Stack>
          </Box>

          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              size="small"
              select
              label="Test with room"
              value={testRoom}
              onChange={(e) => setRoomId(e.target.value)}
              sx={{ minWidth: 200 }}
              SelectProps={{ native: true }}
              disabled={!withToken.length}
            >
              {withToken.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.id}
                  {r.name ? ` · ${r.name}` : ""}
                </option>
              ))}
            </TextField>
            <Button
              variant="contained"
              disabled={busy || !premiseId || !testRoom}
              onClick={test}
              startIcon={busy ? <CircularProgress size={16} /> : undefined}
              sx={{ whiteSpace: "nowrap" }}
              data-testid="vilmer-test"
            >
              Test connection
            </Button>
          </Stack>

          {!premiseId && (
            <Alert severity="info">
              Save the premise once before testing — we fetch on behalf of this
              premise.
            </Alert>
          )}
          {premiseId && !withToken.length && (
            <Alert severity="warning">
              No room has an integration token yet, so there is nothing to test
              with. Add one under Rooms first.
            </Alert>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {result && (
            <Alert severity={result.usingFixtures ? "warning" : "success"}>
              {result.usingFixtures ? (
                <>
                  <strong>Sample data.</strong> No API address is set yet, so
                  this is sample content — {result.activities.length} activities
                  over the next week. Not live.
                </>
              ) : (
                <>
                  <strong>Connected.</strong> {result.activities.length}{" "}
                  activities over the next week
                  {result.unit?.name ? ` for ${result.unit.name}` : ""}.
                </>
              )}
              {result.activities.length > 0 && (
                <Box component="ul" sx={{ m: "8px 0 0", pl: 2.5 }}>
                  {result.activities.slice(0, 5).map((a) => (
                    <li key={a.id}>
                      <Typography variant="body2" component="span">
                        {activityDate(a)} {activityTimeRange(a)} — {a.title}
                        {a.location ? ` (${a.location})` : ""}
                        {a.cancelled ? " · cancelled" : ""}
                      </Typography>
                    </li>
                  ))}
                </Box>
              )}
            </Alert>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}

/** Coded reason → something an operator can act on. Anything unrecognised
 *  stays honest about being unrecognised rather than guessing. */
function vilmerErrorMessage(code: string | undefined): string {
  switch (code) {
    case "vilmer_bad_url":
      return "That is not a valid web address. It must start with https://";
    case "vilmer_blocked_host":
      return "That address points at a private or internal host, which we refuse to fetch.";
    case "vilmer_unauthorized":
      return "VilMer rejected the room's token. Check the token under Rooms, or ask VilMer to reissue it.";
    case "vilmer_unparseable":
      return "VilMer answered, but not with JSON we could read.";
    case "vilmer_too_large":
      return "VilMer's answer was too large to process.";
    case "vilmer_timeout":
      return "VilMer's server did not answer in time.";
    case "vilmer_no_token":
      return "That room has no integration token yet.";
    default:
      return "We could not reach VilMer. Their server answered with an error.";
  }
}
